(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const liveRegion = () => document.getElementById('UrbanLuxLiveRegion');
  const strings = window.UrbanLux?.strings || {};
  let lastDialogTrigger = null;

  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 30);
  };

  const normalise = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const formatMoney = (amount) => {
    const currency = window.UrbanLux?.currency || 'RON';
    const locale = window.UrbanLux?.locale || 'ro-RO';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(Number(amount) / 100);
    } catch (_error) {
      return `${(Number(amount) / 100).toFixed(2)} ${currency}`;
    }
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    lastDialogTrigger = trigger || document.activeElement;
    dialog.showModal();
    dialog.classList.add('is-open');
    document.body.classList.add('modal-open');
    const first = dialog.querySelector(
      '[data-dialog-close], button:not([disabled]), a[href], input:not([disabled]), select:not([disabled])'
    );
    window.setTimeout(() => first?.focus({ preventScroll: true }), 0);
  };

  const closeDialog = (dialog) => {
    if (!dialog?.open) return;
    dialog.classList.remove('is-open');
    dialog.close();
    if (!document.querySelector('dialog[open]')) {
      document.body.classList.remove('modal-open', 'menu-open');
    }
    lastDialogTrigger?.focus?.({ preventScroll: true });
  };

  const setupDialogs = (scope = document) => {
    scope.querySelectorAll('[data-quick-view-open]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.quickViewOpen);
        openDialog(dialog, button);
      });
    });

    scope.querySelectorAll('[data-dialog-close]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => closeDialog(button.closest('dialog')));
    });

    scope.querySelectorAll('dialog').forEach((dialog) => {
      if (dialog.dataset.dialogBound === 'true') return;
      dialog.dataset.dialogBound = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });
      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-open');
        if (!document.querySelector('dialog[open]')) {
          document.body.classList.remove('modal-open', 'menu-open');
        }
      });
    });
  };

  const setupIntro = (scope = document) => {
    const intro = scope.querySelector('[data-cinematic-intro]');
    if (!intro || intro.dataset.bound === 'true') return;
    intro.dataset.bound = 'true';
    const skip = intro.querySelector('[data-intro-skip]');
    let timer;

    const dismiss = () => {
      if (intro.classList.contains('is-dismissed')) return;
      window.clearTimeout(timer);
      intro.classList.add('is-dismissed');
      intro.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('intro-open');
      document.dispatchEvent(new CustomEvent('urbanlux:intro-dismissed'));
      window.setTimeout(() => intro.remove(), 320);
    };

    if (reducedMotion.matches) {
      dismiss();
      return;
    }

    document.body.classList.add('intro-open');
    skip?.focus({ preventScroll: true });
    skip?.addEventListener('click', () => dismiss());
    intro.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaY) > 12) dismiss();
    }, { passive: true, once: true });
    intro.addEventListener('touchmove', () => dismiss(), { passive: true, once: true });
    intro.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' || event.key === 'Enter') dismiss();
      if (event.key === 'Tab') {
        event.preventDefault();
        skip?.focus();
      }
    });
    const duration = Number(intro.dataset.introDuration) || 3600;
    timer = window.setTimeout(() => dismiss(), duration);
  };

  const setupHeader = (scope = document) => {
    const menuButton = scope.querySelector('[data-menu-open]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (menuButton && menu && menuButton.dataset.bound !== 'true') {
      menuButton.dataset.bound = 'true';
      menuButton.addEventListener('click', () => {
        menuButton.setAttribute('aria-expanded', 'true');
        document.body.classList.add('menu-open');
        openDialog(menu, menuButton);
      });
      menu.addEventListener('close', () => {
        menuButton.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('menu-open');
      });
      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => closeDialog(menu));
      });
    }

    scope.querySelectorAll('[data-cart-open]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        openDialog(document.querySelector('[data-cart-drawer]'), button);
      });
    });

    scope.querySelectorAll('[data-localization-open]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        openDialog(document.querySelector('[data-localization-dialog]'), button);
      });
    });
  };

  const setupCursor = () => {
    const cursor = document.querySelector('.custom-cursor');
    if (!cursor || cursor.dataset.bound === 'true') return;
    cursor.dataset.bound = 'true';
    if (reducedMotion.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      cursor.hidden = true;
      return;
    }

    let currentX = -100;
    let currentY = -100;
    let targetX = -100;
    let targetY = -100;
    const label = cursor.querySelector('span');
    const render = () => {
      currentX += (targetX - currentX) * 0.2;
      currentY += (targetY - currentY) * 0.2;
      cursor.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      requestAnimationFrame(render);
    };
    document.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      targetX = event.clientX;
      targetY = event.clientY;
    }, { passive: true });
    document.addEventListener('pointerover', (event) => {
      const target = event.target.closest('[data-cursor]');
      cursor.classList.toggle('is-active', Boolean(target));
      if (label) label.textContent = target?.dataset.cursor || '';
    });
    document.addEventListener('pointerout', (event) => {
      if (event.target.closest('[data-cursor]')) {
        cursor.classList.remove('is-active');
        if (label) label.textContent = '';
      }
    });
    requestAnimationFrame(render);
  };

  const setupCatalog = (scope = document) => {
    scope.querySelectorAll('[data-catalog-section]').forEach((section) => {
      if (section.dataset.bound === 'true') return;
      section.dataset.bound = 'true';
      const cards = Array.from(section.querySelectorAll('[data-product-card]'));
      const filters = Array.from(section.querySelectorAll('[data-catalog-filter]'));
      const search = section.querySelector('[data-catalog-search]');
      const empty = section.querySelector('[data-catalog-empty]');
      const grid = section.querySelector('[data-catalog-grid]');
      const categoryOrder = filters
        .map((button) => normalise(button.dataset.catalogFilter))
        .filter(Boolean);
      let activeFilter = '';

      const categoryIndex = (card) => {
        const categories = normalise(card.dataset.categories).split('|').filter(Boolean);
        const firstMatch = categoryOrder.findIndex((category) => categories.includes(category));
        return firstMatch === -1 ? categoryOrder.length : firstMatch;
      };

      if (grid && categoryOrder.length) {
        cards
          .map((card, originalIndex) => ({ card, originalIndex }))
          .sort((a, b) => categoryIndex(a.card) - categoryIndex(b.card) || a.originalIndex - b.originalIndex)
          .forEach(({ card }) => grid.append(card));
      }

      const apply = () => {
        const query = normalise(search?.value);
        let visible = 0;
        cards.forEach((card) => {
          const categories = normalise(card.dataset.categories).split('|');
          const categoryMatch = !activeFilter || categories.includes(normalise(activeFilter));
          const queryMatch = !query || normalise(card.dataset.search).includes(query);
          const show = categoryMatch && queryMatch;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (empty) empty.hidden = visible !== 0;
        announce((strings.productsDisplayed || '__count__ products displayed').replace('__count__', String(visible)));
      };

      filters.forEach((button) => {
        button.addEventListener('click', () => {
          activeFilter = button.dataset.catalogFilter || '';
          filters.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          apply();
        });
      });
      search?.addEventListener('input', apply);
      section.querySelector('[data-catalog-reset]')?.addEventListener('click', () => {
        activeFilter = '';
        if (search) search.value = '';
        filters.forEach((item, index) => {
          item.classList.toggle('is-active', index === 0);
          item.setAttribute('aria-pressed', String(index === 0));
        });
        apply();
        search?.focus();
      });
    });
  };

  const updateVariantUI = (select) => {
    const form = select.closest('form');
    const rootElement = select.closest('[data-product-root], [data-quick-view]');
    const option = select.selectedOptions[0];
    if (!option) return;
    const available = option.dataset.available !== 'false' && !option.disabled;
    const submit = form?.querySelector('[type="submit"][name="add"]');
    const submitText = submit?.querySelector('[data-add-to-cart-text], span');
    if (submit) submit.disabled = !available;
    if (submitText) submitText.textContent = available
      ? (strings.addToCart || 'Add to cart')
      : (strings.soldOut || 'Sold out');

    const price = rootElement?.querySelector('[data-product-price], [data-variant-price] [data-product-price]');
    if (price && option.dataset.price) {
      price.replaceChildren();
      const current = document.createElement('span');
      current.textContent = option.dataset.price;
      price.append(current);
      if (option.dataset.comparePrice) {
        const compare = document.createElement('s');
        compare.textContent = option.dataset.comparePrice;
        price.append(compare);
      }
    }

    const mediaId = option.dataset.mediaId;
    if (mediaId && rootElement?.matches('[data-product-root]')) {
      rootElement.querySelectorAll('[data-media-id]').forEach((media) => {
        media.classList.toggle('is-active', media.dataset.mediaId === mediaId);
      });
    }
    announce(`${option.textContent.trim()}. ${available ? (strings.available || 'Available') : (strings.soldOut || 'Sold out')}.`);
  };

  const setupProductControls = (scope = document) => {
    scope.querySelectorAll('[data-variant-select]').forEach((select) => {
      if (select.dataset.bound === 'true') return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => updateVariantUI(select));
    });

    scope.querySelectorAll('[data-quantity-change]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const input = button.parentElement?.querySelector('input[type="number"]');
        if (!input) return;
        const delta = Number(button.dataset.quantityChange);
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const step = Number(input.step || 1);
        input.value = String(Math.min(max, Math.max(min, Number(input.value || min) + delta * step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const cartItemNode = (item) => {
    const article = document.createElement('article');
    article.className = 'cart-line';
    article.dataset.lineKey = item.key;

    const imageLink = document.createElement('a');
    imageLink.className = 'cart-line-image';
    imageLink.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image.includes('?') ? `${item.image}&width=240` : `${item.image}?width=240`;
      image.alt = item.product_title || '';
      image.width = 120;
      image.height = 120;
      image.loading = 'lazy';
      imageLink.append(image);
    }

    const copy = document.createElement('div');
    const titleLink = document.createElement('a');
    titleLink.href = item.url;
    const title = document.createElement('h3');
    title.textContent = item.product_title;
    titleLink.append(title);
    copy.append(titleLink);
    if (item.variant_title) {
      const variant = document.createElement('p');
      variant.textContent = item.variant_title;
      copy.append(variant);
    }
    const price = document.createElement('p');
    price.textContent = formatMoney(item.final_line_price);
    copy.append(price);

    const actions = document.createElement('div');
    actions.className = 'cart-line-actions';
    const quantity = document.createElement('input');
    quantity.type = 'number';
    quantity.min = '0';
    quantity.value = String(item.quantity);
    quantity.dataset.cartQuantity = '';
    quantity.setAttribute('aria-label', strings.quantity || 'Quantity');
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.cartRemove = '';
    remove.textContent = strings.remove || 'Remove';
    actions.append(quantity, remove);
    copy.append(actions);
    article.append(imageLink, copy);
    return article;
  };

  const bindCartLines = (scope) => {
    scope.querySelectorAll('[data-cart-quantity]').forEach((input) => {
      if (input.dataset.bound === 'true') return;
      input.dataset.bound = 'true';
      input.addEventListener('change', async () => {
        const line = input.closest('[data-line-key]');
        await changeCart(line?.dataset.lineKey, Math.max(0, Number(input.value || 0)));
      });
    });
    scope.querySelectorAll('[data-cart-remove]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', async () => {
        const line = button.closest('[data-line-key]');
        await changeCart(line?.dataset.lineKey, 0);
      });
    });
  };

  const renderCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
    });
    const drawer = document.querySelector('[data-cart-drawer]');
    const container = drawer?.querySelector('[data-cart-items]');
    const footer = drawer?.querySelector('.drawer-footer');
    if (!container) return;
    container.replaceChildren();
    if (!cart.items.length) {
      const empty = document.createElement('div');
      empty.className = 'drawer-empty';
      const title = document.createElement('h3');
      title.textContent = strings.cartEmpty || 'Your cart is empty.';
      const copy = document.createElement('p');
      copy.textContent = strings.cartEmptyCopy || 'Explore the collection.';
      const link = document.createElement('a');
      link.href = window.UrbanLux?.routes?.allProducts || '/collections/all';
      link.className = 'line-link line-link-dark';
      link.textContent = strings.continueShopping || 'Continue shopping';
      empty.append(title, copy, link);
      container.append(empty);
      if (footer) footer.hidden = true;
    } else {
      cart.items.forEach((item) => container.append(cartItemNode(item)));
      if (footer) footer.hidden = false;
      const subtotal = footer?.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      bindCartLines(container);
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${window.UrbanLux.routes.cart}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await fetchCart();
    renderCart(cart);
    return cart;
  };

  const changeCart = async (id, quantity) => {
    if (!id) return;
    try {
      const response = await fetch(`${window.UrbanLux.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || strings.cartUpdateError || 'The cart could not be updated.');
      renderCart(payload);
      announce(strings.cartUpdated || 'Cart updated.');
    } catch (error) {
      announce(error.message || strings.cartUpdateError || 'The cart could not be updated.');
    }
  };

  const setupAjaxCart = (scope = document) => {
    scope.querySelectorAll('form[data-ajax-cart]').forEach((form) => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = form.querySelector('[type="submit"][name="add"]');
        const message = form.querySelector('[data-form-message]');
        if (submit?.disabled) return;
        submit?.setAttribute('disabled', '');
        form.classList.add('is-loading');
        if (message) message.textContent = '';
        try {
          const response = await fetch(`${window.UrbanLux.routes.cartAdd}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || strings.productAddError || 'The product could not be added.');
          await refreshCart();
          const quickView = form.closest('[data-quick-view]');
          if (quickView) closeDialog(quickView);
          openDialog(document.querySelector('[data-cart-drawer]'), submit);
          announce(strings.productAdded || 'Product added to cart.');
        } catch (error) {
          if (message) message.textContent = error.message;
          announce(error.message || strings.productAddError || 'The product could not be added.');
        } finally {
          submit?.removeAttribute('disabled');
          form.classList.remove('is-loading');
        }
      });
    });
  };

  const setupForms = (scope = document) => {
    scope.querySelectorAll('[data-localization-select]').forEach((select) => {
      if (select.dataset.bound === 'true') return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
    scope.querySelectorAll('[data-sort-select]').forEach((select) => {
      if (select.dataset.bound === 'true') return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  };

  const setupPageTransition = () => {
    if (reducedMotion.matches || document.body.dataset.transitionBound === 'true') return;
    document.body.dataset.transitionBound = 'true';
    document.addEventListener('click', (event) => {
      const anchor = event.target.closest('a[href]');
      if (
        !anchor ||
        anchor.target ||
        anchor.hasAttribute('download') ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) return;
      const url = new URL(anchor.href, window.location.href);
      if (
        url.origin !== window.location.origin ||
        url.hash ||
        anchor.closest('dialog') ||
        /\/products\//.test(url.pathname)
      ) return;
      event.preventDefault();
      document.querySelector('.page-transition')?.classList.add('is-active');
      window.setTimeout(() => window.location.assign(url.href), 430);
    });
  };

  /* Global motion is handled by the dependency-free reveal controller. */
  const setupMotion = () => {};

  const init = (scope = document) => {
    setupIntro(scope);
    setupDialogs(scope);
    setupHeader(scope);
    setupCatalog(scope);
    setupProductControls(scope);
    setupAjaxCart(scope);
    setupForms(scope);
    bindCartLines(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    setupCursor();
    setupPageTransition();
    setupMotion();
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    window.setTimeout(setupMotion, 30);
  });

  document.addEventListener('shopify:section:unload', () => {
    /* Section-specific observers are released by urbanlux-reveals.js. */
  });

  reducedMotion.addEventListener('change', setupMotion);
})();
